import type {
  ComponentProps,
  ComponentType,
  KeyboardEvent,
  ReactElement,
  ReactNode
} from 'react';
import { cloneElement, forwardRef, useEffect, useRef } from 'react';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';
import { useDelayedUnmount } from '@elfsight/lib-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useSwipeable } from 'react-swipeable';
import { Backdrop } from '../backdrop';
import { PopupCloseControl } from '../popup-close-control';
import { Portal } from '../portal';
import { useOutsideClick, useScrollLock } from '../utils';
import { zIndexLayer } from '../zIndexLayer';
import { getSlideAnimations } from './utils/slide-animations';
import { getTranslateValue } from './utils/translate-value';

const ANIMATION_DURATION_MS = 300;
const CLOSING_CLASS = 'pane-wrapper-closing';

export interface PaneContentProps {
  _fixed: boolean;
  _position: Position;
  _isVisible: boolean;
  _animationDuration: number;
  _zIndex: number;
}

export interface PaneProps {
  show: boolean;
  width: number;
  position: Position;
  children?: ReactNode;
  withCloseControl?: boolean;
  closeControlComponent?: ComponentType<
    ComponentProps<typeof PopupCloseControl>
  >;
  closeControlColor?: string;
  withBackdrop?: boolean;
  backdropComponent?: ReactElement;
  backdropColor?: string;
  disableScroll?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  closeOnSwipe?: boolean;
  portalContainerElement?: Element;
  fixed?: boolean;
  zIndex?: number;
  style?: CSSProperties;
  withoutTransition?: boolean;
  onCloseRequest?: () => void;
  ariaLabel?: string;
  shadow?: boolean;
}

export const Pane = forwardRef<HTMLDivElement, PaneProps>(
  (
    {
      children,
      show,
      width,
      position,
      withCloseControl = false,
      closeControlComponent: FinalCloseControl = PopupCloseControl,
      closeControlColor,
      withBackdrop = false,
      backdropComponent,
      backdropColor,
      portalContainerElement,
      disableScroll = false,
      closeOnOutsideClick = false,
      closeOnEscape = false,
      closeOnSwipe = false,
      fixed = false,
      zIndex = zIndexLayer.modal,
      style,
      withoutTransition = false,
      onCloseRequest,
      ariaLabel,
      shadow
    },
    forwardingRef
  ) => {
    const animationDuration = withoutTransition ? 0 : ANIMATION_DURATION_MS;
    const [isMounted, isClosing] = useDelayedUnmount(show, animationDuration);
    const isVisible = show && !isClosing;

    const requestClose = () => {
      if (show && onCloseRequest) {
        onCloseRequest();
      }
    };

    const { ref: contentSwipeableRef } = useSwipeable({
      onSwiped: ({ dir, velocity }) => {
        if (closeOnSwipe && dir.toLowerCase() === position && velocity > 0.5) {
          requestClose();
        }
      }
    });

    const outsideClickRef = useOutsideClick(() => {
      if (closeOnOutsideClick) {
        requestClose();
      }
    });

    const contentMergedRef = useMergedRef(outsideClickRef, contentSwipeableRef);

    const handleWrapperKeydown = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        requestClose();
      }
    };

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (wrapperRef.current && show) {
        wrapperRef.current.focus({ preventScroll: true });
      }
    }, [show]);

    useScrollLock(wrapperRef, disableScroll && show);

    const wrapperMergedRef = useMergedRef(forwardingRef, wrapperRef);

    const FinalBackdrop = backdropComponent ? (
      cloneElement(backdropComponent, {
        ...backdropComponent.props,
        style: {
          zIndex,
          ...backdropComponent.props.style
        }
      })
    ) : (
      <Backdrop
        background={backdropColor}
        fixed={fixed}
        zIndex={zIndex}
        open={show}
        withoutTransition={withoutTransition}
      />
    );

    return (
      <Portal containerElement={portalContainerElement} shadow={shadow}>
        {isMounted && (
          <PaneWrapper
            ref={wrapperMergedRef}
            tabIndex={-1}
            aria-label={ariaLabel}
            role="dialog"
            style={{ outline: 0 }}
            onKeyDown={handleWrapperKeydown}
            aria-modal
          >
            {withBackdrop && FinalBackdrop}

            <PaneContent
              _zIndex={zIndex}
              _isVisible={isVisible}
              _animationDuration={animationDuration}
              _fixed={fixed}
              _position={position}
              ref={contentMergedRef}
              className={isVisible ? '' : CLOSING_CLASS}
              style={{ ...style, width }}
            >
              {withCloseControl && (
                <PaneClose>
                  <FinalCloseControl
                    color={closeControlColor}
                    onClick={requestClose}
                  />
                </PaneClose>
              )}

              {children}
            </PaneContent>
          </PaneWrapper>
        )}
      </Portal>
    );
  }
);

type Position = 'left' | 'right';

const PaneWrapper = styled.div``;

export const PaneContent = styled.div<PaneContentProps>`
  ${({ _fixed, _zIndex, _position, _isVisible, _animationDuration }) => css`
    position: ${_fixed ? 'fixed' : 'absolute'};
    bottom: 0;
    max-width: calc(100% - 40px);
    height: 100%;
    overflow: auto;
    top: 0;
    z-index: ${_zIndex};
    ${_position === 'right' ? 'right: 0;' : 'left: 0;'};
    animation: ${getSlideAnimations(_position, _isVisible)}
      ${_animationDuration}ms ease-out forwards;

    @media (prefers-reduced-motion: reduce) {
      & {
        animation: none;
        translate: ${getTranslateValue(_position, _isVisible)};
      }
    }
  `}
`;

export const PaneClose = styled.div`
  position: absolute;
  z-index: 10;
  top: 12px;
  right: 12px;
`;
